/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { BasicFunction } from '../../helpers/jsHelpers/types';

import HotKeys from '../../helpers/hotKeys/hotKeys';
import Localizer from '../../helpers/localization';
import { SEARCH } from '../../helpers/search/constants';
import { PictoMagnifier, PictoX } from '../pictos/Element';
import './SearchBox.css';

export type SearchBoxHandle = {
  isClosed: () => boolean,
  open: BasicFunction,
};

type SearchBoxPropType = {
  +onSearch: (searchString: string, isFocusHidden: boolean) => void,
};

const SearchBox = ({ onSearch }: SearchBoxPropType, ref: React.RefSetter<SearchBoxHandle>): React.Node => {
  const lastSearchTerm: string = useSelector((state) => state.ui.searchHistory[0]);
  const isInTheaterMode = useSelector<boolean>((state) => state.ui.isInTheaterMode);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFocusHidden, setIsFocusHidden] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');

  const inputRef = useRef<HTMLElement | null>(null);

  const isClosed = useCallback(() => !isOpen, [isOpen]);
  const open = useCallback(() => setIsOpen(true), [setIsOpen]);

  useImperativeHandle(ref, (): SearchBoxHandle => {
    return {
      isClosed,
      open,
    };
  });

  const reset: () => void = useCallback(() => {
    HotKeys.unregister('enter', handleSearchHotKey);
    HotKeys.unregister('escape', handleResetHotKey);

    setIsOpen(false);
    setIsFocusHidden(false);
    setSearchString('');
  }, [setIsOpen, setSearchString]);

  const launchSearch = useCallback(
    (searchTerm?: string) => {
      const trimmedSearchString = searchTerm ?? searchString.trim();
      if (trimmedSearchString.length >= SEARCH.StringMinLength) {
        onSearch(trimmedSearchString, isFocusHidden);
        reset();
      }
    },
    [isFocusHidden, onSearch, reset, searchString],
  );

  const handleSearchHotKey = useCallback(
    (event: SyntheticKeyboardEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      launchSearch();
    },
    [launchSearch],
  );

  const handleResetHotKey = useCallback(
    (event: SyntheticKeyboardEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      reset();
    },
    [reset],
  );

  useEffect(() => {
    if (!isOpen || !inputRef.current) {
      // Returning undefined to avoid eslint consistent-return error
      return undefined;
    }

    inputRef.current.focus();

    HotKeys.register('enter', handleSearchHotKey, {
      allowEditableContext: true,
      name: 'Search.search',
    });
    HotKeys.register('escape', handleResetHotKey, {
      allowEditableContext: true,
      name: 'Search.reset',
    });

    return () => {
      HotKeys.unregister('enter', handleSearchHotKey);
      HotKeys.unregister('escape', handleResetHotKey);
    };
  }, [handleResetHotKey, handleSearchHotKey, isOpen]);

  const handleOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      const { altKey, ctrlKey, shiftKey, type } = event;

      if (!isOpen) {
        if (altKey || ctrlKey || shiftKey) {
          // Relaunch last search
          launchSearch(lastSearchTerm);
        } else {
          // Even if it's a click event, user can open the search box by navigating to the magnifier picto and press Enter or Space
          setIsOpen(true);
          setIsFocusHidden(type === 'click');
        }
      } else {
        launchSearch();
      }
    },
    [isOpen, lastSearchTerm, launchSearch, setIsOpen],
  );

  const handleOnChange = useCallback(
    (event: SyntheticInputEvent<HTMLInputElement>) => {
      setSearchString(event.currentTarget.value);
    },
    [setSearchString],
  );

  const handleOnBlur = useCallback(() => {
    if (searchString.trim() === '') {
      reset();
    }
  }, [reset, searchString]);

  const handleClearOnClick = useCallback(() => {
    setSearchString('');
  }, [reset, setSearchString]);

  const inputElt = useMemo(() => {
    if (!isOpen) {
      return null;
    }

    return (
      <>
        <label className='screenReaderOnly' htmlFor='searchbox-input'>
          {Localizer.localize('accessibility.navigation.search_label')}
        </label>
        <div className='inputContainer'>
          <input
            className={clsx(isFocusHidden && 'hiddenFocus')}
            id='searchbox-input'
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            placeholder={Localizer.localize('search.placeholder')}
            ref={inputRef}
            type='search'
            value={searchString}
          />
          <PictoX aria-label={Localizer.localize('accessibility.navigation.clear_search')} className='clearButton' onClick={handleClearOnClick} />
        </div>
      </>
    );
  }, [handleClearOnClick, handleOnBlur, handleOnChange, isFocusHidden, isOpen, searchString]);

  if (isInTheaterMode) {
    // Prevent screen reader from reading the search box label when player is visible
    return null;
  }

  // If closed: render inside a simple container
  if (!isOpen) {
    return (
      <div className='searchBox'>
        <PictoMagnifier aria-label={Localizer.localize('accessibility.navigation.search')} id='search-button' onClick={handleOnClick} />
      </div>
    );
  }

  // If open: wrap the active search controls inside <search> landmark
  return (
    <search className='searchBox'>
      <PictoMagnifier aria-label={Localizer.localize('accessibility.navigation.search')} id='search-button' onClick={handleOnClick} />
      {inputElt}
    </search>
  );
};

export default React.forwardRef<SearchBoxPropType, SearchBoxHandle>(SearchBox) as React.ComponentType<{
  ...SearchBoxPropType,
  +ref?: React.RefSetter<SearchBoxHandle> | { current: SearchBoxHandle | null, ... } | null,
}>;
